import ModalComponent from '../modal/modal';
import styles from './userVerificationModal.module.css';
import { validateAndDecodeAuthToken } from '../../utils/helper';
import {
  AuthTokenResponse,
  LoginAsStaffResponseData,
} from '../../service/service.type';
import { GetUsersResponse, UserType } from '@inplay/models';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { useAuthUsersSummary, useLoginAsStaff } from '../../hooks/useUserData';
import {
  SelectedUser,
  UserVerificationModalProps,
} from './userVerificationModal.types';
import { FC, useEffect, useState } from 'react';
import { Loader } from './contents/modalLoader/modalLoader';
import { VerificationErrorModalContent } from './contents/verificationErrorModal/verificationErrorModal';
import { ForgotPinContent } from './contents/forgotPin/forgotPin';
import { CodeVerificationContent } from './contents/codeVerification/codeVerification';
import { SelectUserContent } from './contents/selectUser/selectUser';
import {
  useSendUserVerificationCode,
  useVerifyUserVerificationCode,
} from '../../hooks/useAuthData';
import { apiStatusCode } from '../../utils/helper.type';

const UserVerificationModal: FC<UserVerificationModalProps> = (
  props: UserVerificationModalProps,
) => {
  const [code, setCode] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<SelectedUser>();
  const [showCodeStage, setShowCodeStage] = useState<boolean>(false);
  const [showVerificationErrorModal, setShowVerificationErrorModal] =
    useState<boolean>(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [showForgotPinModal, setShowForgotPinModal] = useState<boolean>(false);
  const validAuthToken: AuthTokenResponse | null = validateAndDecodeAuthToken();
  const { selectedVenueBranch } = useOrganizationContext();
  const { authUsersSummary, authUsersLoading } = useAuthUsersSummary(
    selectedVenueBranch?._id || '',
    props.scope,
  );
  const { sendOtp, sendOtpLoading, sessionId } = useSendUserVerificationCode();
  const { verifyOtp, verifyOtpLoading, verified, error, setError, attemptsRemaining, expired } =
    useVerifyUserVerificationCode();
  const { loginAsStaffLoading, loginAsStaff } = useLoginAsStaff();

  useEffect(() => {
    if (
      authUsersSummary &&
      !authUsersLoading &&
      validAuthToken?.email
    ) {
      setShowVerificationErrorModal(false);
      setSelectedUser({
        email: validAuthToken.email,
        firstName: validAuthToken.firstName,
        lastName: validAuthToken.lastName,
        type: validAuthToken.type,
        userId: authUsersSummary.filter(
          (authUser: GetUsersResponse) =>
            authUser.email === validAuthToken.email,
        )[0].id,
      });
    }
  }, [authUsersLoading, authUsersSummary]);

  const handleBack = () => {
    setShowCodeStage(false);
    setCode('');
  };

  const handleOnSelectUser = (userId: string) => {
    const user: GetUsersResponse | undefined = authUsersSummary?.find(
      (user) => user.id === userId,
    );
    if (user?.id) {
      setSelectedUser({
        email: user.email ?? '',
        firstName: user.firstName,
        lastName: user.lastName,
        type: user.type,
        userId: user.id,
      });
    } else {
      setSelectedUser(undefined);
    }
  };

  const handleResetForgotCode = () => {
    setCode('');
    if (
      selectedUser?.type === UserType.VENUE_STAFF ||
      selectedUser?.type === UserType.VENUE_BRANCH_LOGIN
    ) {
      setShowForgotPinModal(true);
    } else {
      handleUserVerificationNext();
    }
  };

  const handleVerificationErrorOk = () => {
    setShowVerificationErrorModal(false);
    setShowCodeStage(true);
    setCode('');
  };

  const handleForgotPinModalOk = () => {
    setShowForgotPinModal(false);
    setShowCodeStage(true);
    setCode('');
  };

  const handleUserVerificationNext = () => {
    if (selectedUser?.userId) {
      if (
        selectedUser?.type === UserType.VENUE_STAFF ||
        selectedUser?.type === UserType.VENUE_BRANCH_LOGIN
      ) {
        setShowCodeStage(true);
      } else {
        sendOtp(selectedUser.email, selectedUser.userId);
      }
    }
  };

  useEffect(() => {
    if (!sendOtpLoading && sessionId) {
      setShowCodeStage(true);
    }
  }, [sendOtpLoading, sessionId]);

  useEffect(() => {
    if (code.length > 0) setInlineError(null);
    if (
      selectedUser?.type === UserType.VENUE_STAFF ||
      selectedUser?.type === UserType.VENUE_BRANCH_LOGIN
    ) {
      if (code.length === 4) {
        handleCodeVerification();
      }
    } else if (code.length === 6) {
      handleCodeVerification();
    }
  }, [code]);

  const handleCodeVerification = async () => {
    if (
      selectedUser?.type === UserType.VENUE_STAFF ||
      selectedUser?.type === UserType.VENUE_BRANCH_LOGIN
    ) {
      const staffLoginResponse: LoginAsStaffResponseData | undefined =
        await loginAsStaff(code);
      if (staffLoginResponse?.statusCode === apiStatusCode.SUCCESS) {
        props.onToggle();
        props.onVerified?.(selectedUser);
      } else {
        setInlineError("Invalid OTP. Please try again with the correct code.");
        setCode("");
      }
    } else {
      if (!sessionId || !selectedUser?.userId) return;
      verifyOtp(code, sessionId, selectedUser.userId);
    }
  };

  useEffect(() => {
    if (verified) {
      props.onToggle();
      props.onVerified?.(selectedUser!);
    } else if (!verified && error) {
      if (error === "An unexpected error occurred. Please try again later.") {
        setShowVerificationErrorModal(true);
      } else if (expired || attemptsRemaining === 0) {
        setInlineError("PIN has expired after 3 failed attempts.");
        setCode("");
        setTimeout(() => props.onToggle(), 3000);
      } else {
        const msg = "Invalid OTP. Please try again with the correct code."
        setInlineError(msg);
        setCode("");
      }
    }

    if (error) setError(null);
  }, [verified, error]);

  if (!props.show) return <></>;

  if (
    authUsersLoading ||
    sendOtpLoading ||
    verifyOtpLoading ||
    loginAsStaffLoading
  ) {
    return <Loader show={props.show} onToggle={props.onToggle} />;
  }

  return (
    <div className={styles.userVerificationOverlay}>
      <ModalComponent
        title=""
        open={props.show}
        onToggle={props.onToggle}
        isShowClose={!showVerificationErrorModal && !showForgotPinModal}
        noHeaderBorder
        centered
        dialogClassName="w-[clamp(400px,50%,600px)]"
        showBackButton={
          showCodeStage && !showVerificationErrorModal && !showForgotPinModal
        }
        onBackButtonClick={handleBack}
      >
        {showForgotPinModal ? (
          <ForgotPinContent handleForgotPinModalOk={handleForgotPinModalOk} />
        ) : showVerificationErrorModal ? (
          <VerificationErrorModalContent
            handleVerificationErrorOk={handleVerificationErrorOk}
          />
        ) : showCodeStage && selectedUser ? (
          <CodeVerificationContent
            selectedUser={selectedUser}
            code={code}
            setCode={setCode}
            setShowForgotPinModal={setShowForgotPinModal}
            handleCodeVerification={handleCodeVerification}
            handleResetForgotCode={handleResetForgotCode}
            errorMessage={inlineError}
          />
        ) : (
          <SelectUserContent
            authUsersSummary={authUsersSummary}
            validAuthToken={validAuthToken}
            selectedUser={selectedUser}
            handleOnSelectUser={handleOnSelectUser}
            handleNext={handleUserVerificationNext}
          />
        )}
      </ModalComponent>
    </div>
  );
};

export default UserVerificationModal;
